import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import BrandMark from '../components/branding/BrandMark';
import ConfirmDialog from '../components/feedback/ConfirmDialog';
import {
  AccountIcon,
  CatalogIcon,
  DriversIcon,
  ChevronDownIcon,
  HomeIcon,
  InfoIcon,
  MoonIcon,
  PointsIcon,
  ReportsIcon,
  SignInIcon,
  SignOutIcon,
  SunIcon,
  UserIcon,
  UsersIcon,
  ViewingAsIcon,
} from '../components/primitives/Icons';
import { MfaSetupWall } from '../features/accounts';
import { DeviceCheckDialog } from '../features/authentication';
import './AppLayout.css';
import ActiveViewAsIndicator from './ActiveViewAsIndicator';
import { navItemsFor } from './navigation';
import NotificationBell from './NotificationBell';
import { PageHeaderTargetProvider } from './PageHeader';
import { getTheme, saveTheme } from './theme';

const NAV_ICONS = {
  home: HomeIcon,
  points: PointsIcon,
  drivers: DriversIcon,
  catalog: CatalogIcon,
  users: UsersIcon,
  reports: ReportsIcon,
  about: InfoIcon,
};

function ThemeToggle() {
  const [theme, setTheme] = useState(getTheme);
  const nextTheme = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      className="theme-toggle"
      type="button"
      aria-label={`Switch to ${nextTheme} mode`}
      title={`Switch to ${nextTheme} mode`}
      onClick={() => {
        saveTheme(nextTheme);
        setTheme(nextTheme);
      }}
    >
      {theme === 'dark' ? <SunIcon size={18} /> : <MoonIcon size={18} />}
    </button>
  );
}

function AccountMenu({ user, onSignOut }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const menuRef = useRef(null);
  const closeTimerRef = useRef(null);
  const openedByHoverRef = useRef(false);

  const keepMenuOpen = () => {
    window.clearTimeout(closeTimerRef.current);
    if (!open) openedByHoverRef.current = true;
    setOpen(true);
  };

  const scheduleMenuClose = () => {
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => {
      openedByHoverRef.current = false;
      setOpen(false);
    }, 220);
  };

  const toggleMenu = () => {
    window.clearTimeout(closeTimerRef.current);
    if (openedByHoverRef.current) {
      openedByHoverRef.current = false;
      setOpen(true);
      return;
    }
    setOpen((current) => !current);
  };

  useEffect(() => {
    if (!open) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        openedByHoverRef.current = false;
        setOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        openedByHoverRef.current = false;
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
      window.clearTimeout(closeTimerRef.current);
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
    setConfirming(false);
  }, [user?.username, user?.impersonation?.active]);

  const confirmSignOut = async () => {
    setSigningOut(true);
    try {
      await onSignOut();
    } finally {
      setSigningOut(false);
      setConfirming(false);
    }
  };
  if (user) {
    const displayName = user.name || user.username;
    return (
      <div
        className="profile-menu-shell"
        ref={menuRef}
        onMouseEnter={keepMenuOpen}
        onMouseLeave={scheduleMenuClose}
      >
        <button
          aria-expanded={open}
          aria-haspopup="menu"
          aria-label={`Profile menu for ${displayName}`}
          className="profile-menu-trigger"
          type="button"
          onClick={toggleMenu}
        >
          <UserIcon size={21} />
          <span className="profile-trigger-name">{displayName}</span>
          <ChevronDownIcon className="profile-menu-chevron" size={14} />
        </button>
        {open && (
          <div className="profile-menu" role="menu">
            {user.impersonation?.active && (
              <>
                <div className="profile-menu-view-as">
                  <ViewingAsIcon size={18} />
                  <div>
                    <span>Viewing as</span>
                    <strong>{displayName}</strong>
                    <span>@{user.username}</span>
                  </div>
                </div>
                <div className="profile-menu-signed-in">
                  <span>Signed in as</span>
                  <strong>{user.impersonation.admin.name}</strong>
                  <span>@{user.impersonation.admin.username}</span>
                </div>
                <div className="profile-menu-divider" />
              </>
            )}
            <div className="profile-menu-identity">
              {user.impersonation?.active && <span>Current account</span>}
              <strong>{displayName}</strong>
              <span>@{user.username}</span>
            </div>
            <div className="profile-menu-divider" />
            <Link role="menuitem" to="/account" onClick={() => setOpen(false)}>
              <AccountIcon size={18} />
              Account
            </Link>
            <button role="menuitem" type="button" onClick={() => setConfirming(true)}>
              <SignOutIcon size={18} />
              Sign out
            </button>
          </div>
        )}
        {confirming && (
          <ConfirmDialog
            title="Sign out?"
            message={
              user.impersonation?.active
                ? "This will end your View-as session and sign you out completely. You'll need to sign in again to keep using your account."
                : "Are you sure you want to sign out? You'll need to sign in again to keep using your account."
            }
            confirmLabel={signingOut ? 'Signing out…' : 'Sign out'}
            busy={signingOut}
            onConfirm={confirmSignOut}
            onCancel={() => setConfirming(false)}
          />
        )}
      </div>
    );
  }

  return (
    <Link className="topbar-signin" to="/login">
      <SignInIcon size={16} />
      Sign in
    </Link>
  );
}

export function AppLayout() {
  const { user, notice, signOut, stopImpersonation, answerDeviceCheck, clearNotice } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [endingViewAs, setEndingViewAs] = useState(false);
  const [viewAsError, setViewAsError] = useState('');
  const [identityAnnouncement, setIdentityAnnouncement] = useState('');
  const [pageHeaderTarget, setPageHeaderTarget] = useState(null);
  const viewAsReturnToRef = useRef('/users');
  const previousViewAsRef = useRef(Boolean(user?.impersonation?.active));
  const mfaWallNeeded =
    user &&
    (user.account_type === 'sponsor' || user.account_type === 'admin') &&
    user.mfa &&
    user.mfa.required &&
    !user.mfa.enrolled;
  const showBell =
    !mfaWallNeeded && (user?.account_type === 'driver' || user?.account_type === 'sponsor');

  // A timed-out session goes straight to sign-in, even from public pages.
  useEffect(() => {
    if (notice === 'expired' && !user) navigate('/login', { replace: true });
  }, [notice, user, navigate]);

  useEffect(() => {
    if (!user?.impersonation?.active) return;
    const candidate = location.state?.viewAsReturnTo || location.pathname;
    if (/^\/users\/(drivers|sponsors)\/\d+$/.test(candidate)) {
      viewAsReturnToRef.current = candidate;
    }
  }, [location.pathname, location.state, user?.impersonation?.active]);

  useEffect(() => {
    const active = Boolean(user?.impersonation?.active);
    if (active && !previousViewAsRef.current) {
      setIdentityAnnouncement(
        `Now viewing as ${user.name || user.username}, ${user.account_type}. You're still signed in as ${user.impersonation.admin.name}.`,
      );
    }
    previousViewAsRef.current = active;
  }, [user]);

  useEffect(() => {
    if (notice !== 'view-as-ended') return;
    setIdentityAnnouncement(
      "Your View-as session has ended. You've returned to your administrator account.",
    );
    navigate('/users', { replace: true });
  }, [notice, navigate]);

  const stopViewingAs = async () => {
    setEndingViewAs(true);
    setViewAsError('');
    try {
      await stopImpersonation();
      setIdentityAnnouncement("You're back in your administrator account.");
      navigate(viewAsReturnToRef.current, { replace: true });
      viewAsReturnToRef.current = '/users';
    } catch (error) {
      setViewAsError(error.message || 'Could not return to your admin account.');
    } finally {
      setEndingViewAs(false);
    }
  };

  return (
    <div className="app-shell">
      <p className="sr-only" aria-live="polite" aria-atomic="true">
        {identityAnnouncement}
      </p>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <aside className="site-sidebar">
        <div className="brand-bar">
          <Link className="brand" to="/" aria-label="Good Driver home">
            <BrandMark />
            <span className="brand-name">Good Driver</span>
          </Link>
          {showBell && (
            <NotificationBell key={user.username} viewingAs={Boolean(user.impersonation?.active)} />
          )}
        </div>
        <nav className="site-nav" aria-label="Primary navigation">
          {navItemsFor(user).map((item) => {
            const Icon = NAV_ICONS[item.icon];
            return (
              <NavLink key={item.to} to={item.to} end={item.to === '/'}>
                <Icon className="nav-icon" size={16} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="sidebar-account">
          <AccountMenu user={user} onSignOut={signOut} />
        </div>
      </aside>
      <div className="app-main">
        {user?.impersonation?.active && (
          <ActiveViewAsIndicator
            user={user}
            busy={endingViewAs}
            error={viewAsError}
            onReturn={stopViewingAs}
          />
        )}
        {user?.session?.device_check && (
          <DeviceCheckDialog onAnswer={answerDeviceCheck} onSignOut={signOut} />
        )}
        <header className="app-topbar">
          <div className="app-topbar-page" ref={setPageHeaderTarget} />
          <ThemeToggle />
        </header>
        {(notice === 'view-as-returned' || notice === 'view-as-ended') && (
          <div className="banner banner-warning view-as-ended-notice">
            <span>
              {notice === 'view-as-returned'
                ? "You're back in your administrator account."
                : 'Your View-as session has ended. You have returned to your administrator account.'}
            </span>
            <button className="button" type="button" onClick={clearNotice}>
              Dismiss
            </button>
          </div>
        )}
        <div className="app-content" id="main-content" tabIndex="-1">
          <PageHeaderTargetProvider target={pageHeaderTarget}>
            {mfaWallNeeded ? (
              <MfaSetupWall />
            ) : (
              <>
                {user && user.mfa && user.mfa.required && !user.mfa.enrolled && (
                  <div className="mfa-required-banner">
                    Your sponsor requires two-factor authentication. Set it up to keep signing in
                    without interruption. <Link to="/account">Set up now</Link>
                  </div>
                )}
                <Outlet />
              </>
            )}
          </PageHeaderTargetProvider>
        </div>
      </div>
    </div>
  );
}
